"use client";
import React from "react";

interface CTextAreaProps {
  id?: string;
  name?: string;
  placeholder?: string;
  className?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  label?: React.ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  width?: string;
  height?: string;
  rows?: number;
  cols?: number;
  maxLength?: number;
  minLength?: number;
  wrap?: "soft" | "hard" | "off";
  resize?: "none" | "both" | "horizontal" | "vertical";
  errorQuery?: boolean;
  errorMessage?: string;
  title?: string;
  role?: "client" | "admin";
  style?: React.CSSProperties;
}

const CTextArea: React.FC<CTextAreaProps> = ({
  id = "",
  name = "",
  placeholder = "",
  className = "",
  value,
  defaultValue,
  onChange,
  label,
  disabled = false,
  readOnly = false,
  required = false,
  autoFocus = false,
  width,
  height,
  rows = 3,
  cols,
  maxLength,
  minLength,
  wrap = "soft",
  resize = "vertical",
  errorQuery = false,
  errorMessage = "Invalid input",
  title = "",
  role,
  style,
  ...rest
}) => {
  // Get user role from localStorage if not provided
  let detectedRole = "admin";
  if (typeof window !== "undefined") {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const userObj = JSON.parse(userStr);
        detectedRole = userObj?.user_type === "client" ? "client" : "admin";
      }
    } catch {}
  }

  // Only use string label for id
  const stringLabel = typeof label === "string" ? label : "";
  const inputId = id || name || stringLabel.toLowerCase().replace(/\s+/g, "-");

  const resizeClass = {
    none: "resize-none",
    both: "resize",
    horizontal: "resize-x",
    vertical: "resize-y",
  }[resize];

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="text-sm text-gray-500 mb-1 block">
          {label}
        </label>
      )}

      <textarea
        id={inputId}
        name={name}
        title={title}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        placeholder={placeholder || stringLabel}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
        rows={rows}
        cols={cols}
        maxLength={maxLength}
        minLength={minLength}
        wrap={wrap}
        style={style}
        className={`
          ${className}
          ${width || "w-full"}
          ${resizeClass}
          border rounded-md px-3 py-2 outline-none placeholder:italic placeholder:text-sm placeholder:text-muted-foreground/60
          bg-transparent
          ${errorQuery ? "border-destructive focus:border-destructive focus:ring-1 focus:ring-destructive" : "border-border/50 focus:border-primary focus:ring-1 focus:ring-primary"}
          ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-border"}
          transition-colors duration-200
        `}
        {...rest}
      />

      {errorQuery && (
        <p className="text-destructive text-xs mt-1">{errorMessage}</p>
      )}
    </div>
  );
};

export default CTextArea;
